/**
 * 業務のロジック図の骨組み — 業務 1 件の処理を、6 種類の節点と線で表す。
 *
 * 節点の種類は Figma の凡例に合わせる（Dhik 2026-10-01）。入力 ・ 業務ルール ・ 業務ルールの出力 ・
 * 分岐 ・ 作用 ・ 終了。**種類は形ではなくタグで見分ける**（「図形は適当なのでいい感じにタグで差し替えたい」）。
 * 箱の形は揃え、上端のタグで種類を読ませる。
 *
 * 線は向きだけを持ち、分岐から出る線には条件のラベル（いる ／ いない、0 ／ 1 以上）を付ける。業務ルールの
 * 中身（判定表 ・ 並べ替えのキー ・ 式）は図に入れない。図の節点は Figma の図と 1 対 1 にし、中身は業務ルールの
 * 箱を押して開く（Dhik 2026-10-01「表は業務ルールを出しただけで図に入れてほしい意図ではない」）。
 *
 * 縦の置き場所は `lane`（段）で持つ。0 が本筋で、数が大きいほど下に置く。横の置き場所は線の深さから
 * 決める（`logic-layout.ts`）。段を材料に持たせるのは、本筋と脇道の上下を材料の側で決め、同じ材料からは
 * 毎回同じ図にするためである。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務のロジック図の材料（6 種類の節点・条件つきの線）の形を定める。
 * @graph-connects none
 */

/**
 * 節点の種類。並びは凡例の順。
 * @graph-connects none
 */
export const LOGIC_NODE_KINDS = ['input', 'rule', 'ruleOutput', 'branch', 'effect', 'end'] as const;

/**
 * 節点の種類。
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business ロジック図の節点の種類（入力・業務ルール・業務ルールの出力・分岐・作用・終了）を表す。
 * @graph-connects none
 */
export type LogicNodeKind = (typeof LOGIC_NODE_KINDS)[number];

/**
 * 節点 1 つ。
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business ロジック図の節点 1 つ（鍵・種類・名前・中の行・段・押したときに開く業務ルール）を表す。
 * @graph-connects none
 */
export interface LogicNode {
  /** 図の中で一意な鍵 */
  readonly key: string;
  /** 種類 */
  readonly kind: LogicNodeKind;
  /** 名前。入力の束のように名前を持たない節点は空文字 */
  readonly title: string;
  /** 中に並べる行（入力の束の用語、終了の遷移先と返り値） */
  readonly lines: readonly string[];
  /** 縦の段。0 が本筋 */
  readonly lane: number;
  /** 押したときに開く業務ルールの番号（分岐は結末を決める判定表、取り出しは並べ替え）。押せなければ省く */
  readonly ruleId?: number;
}

/**
 * 線 1 本。
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business ロジック図の線 1 本（出し手・受け手・分岐の条件のラベル）を表す。
 * @graph-connects none
 */
export interface LogicEdge {
  /** 出し手の節点の鍵 */
  readonly from: string;
  /** 受け手の節点の鍵 */
  readonly to: string;
  /** 分岐の条件。分岐から出る線にだけ付ける */
  readonly label?: string;
}

/**
 * ロジック図 1 枚ぶんの材料。
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business ロジック図 1 枚ぶんの節点・線を束ねる。
 * @graph-connects none
 */
export interface LogicDiagramModel {
  readonly nodes: readonly LogicNode[];
  readonly edges: readonly LogicEdge[];
}

/**
 * 材料の食い違い（節点の鍵が重なる、線の端が無い節点を指す）を並べる。
 *
 * 食い違いのある材料を描くと、線が宙に浮く。画面は描く前にこれで確かめ、テストは
 * mock がこれで 0 件になることを見る。
 *
 * @param model 材料
 * @returns 食い違いの文（無ければ空）
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business ロジック図の材料で、節点の鍵が重ならないかと、線の端が実在の節点を指しているかを検める。
 * @graph-connects none
 */
export function findLogicModelIssues(model: LogicDiagramModel): string[] {
  const keys = new Set(model.nodes.map((node) => node.key));
  const issues: string[] = [];
  const seen = new Set<string>();
  for (const node of model.nodes) {
    if (seen.has(node.key)) issues.push(`節点の鍵 ${node.key} が 2 度ある`);
    seen.add(node.key);
  }
  for (const edge of model.edges) {
    if (!keys.has(edge.from)) issues.push(`線の出し手 ${edge.from} が無い`);
    if (!keys.has(edge.to)) issues.push(`線の受け手 ${edge.to} が無い`);
  }
  return issues;
}

/**
 * 変更前と変更後の材料を合わせる（案件の左右比較で 2 面の置き場所を 1 つに決めるため）。
 *
 * 節点は鍵で 1 つにし、変更後を優先する。線は出し手 ・ 受け手 ・ ラベルの組で 1 本にする。
 *
 * @param before 変更前の材料
 * @param after 変更後の材料
 * @returns 合わせた材料
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 案件の変更前と変更後のロジック図の材料を、節点・線の鍵で重ねて 1 つにする。
 * @graph-connects none
 */
export function mergeLogicModels(
  before: LogicDiagramModel,
  after: LogicDiagramModel,
): LogicDiagramModel {
  const nodes = new Map(before.nodes.map((node) => [node.key, node]));
  for (const node of after.nodes) nodes.set(node.key, node);
  const edges = new Map(
    [...before.edges, ...after.edges].map((edge) => [
      `${edge.from}>${edge.to}>${edge.label ?? ''}`,
      edge,
    ]),
  );
  return { nodes: [...nodes.values()], edges: [...edges.values()] };
}

/**
 * 片側の材料にだけある節点の鍵。
 *
 * @param here 見ている面の材料
 * @param other もう片方の面の材料
 * @returns 見ている面にだけある節点の鍵
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business ロジック図の 2 面のうち、見ている面にだけある節点の鍵を集める（案件の印に使う）。
 * @graph-connects none
 */
export function logicKeysOnlyIn(
  here: LogicDiagramModel,
  other: LogicDiagramModel,
): ReadonlySet<string> {
  const others = new Set(other.nodes.map((node) => node.key));
  return new Set(here.nodes.map((node) => node.key).filter((key) => !others.has(key)));
}

/**
 * Map から鍵で引き、無ければ既定値を返す。置き場所の計算で、鍵が必ずある引き直しを 1 か所に寄せる。
 *
 * @param map 引く Map
 * @param key 鍵
 * @param fallback 無いときの値
 * @returns 引いた値か既定値
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business Map を鍵で引き、無ければ既定値を返す（ロジック図の置き場所の計算の引き直し）。
 * @graph-connects none
 */
export function getOr<K, V>(map: ReadonlyMap<K, V>, key: K, fallback: V): V {
  return map.get(key) ?? fallback;
}

/**
 * 配列を位置で引き、無ければ既定値を返す。
 *
 * @param list 引く配列
 * @param index 位置
 * @param fallback 無いときの値
 * @returns 引いた値か既定値
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 配列を位置で引き、無ければ既定値を返す（ロジック図の置き場所の計算の引き直し）。
 * @graph-connects none
 */
export function atOr<T>(list: readonly T[], index: number, fallback: T): T {
  return list[index] ?? fallback;
}
